import type { CipherDirection } from "@/lib/tool-kit/ciphers-polygraphic/keyed-letters";
import {
  buildSquare,
  type GridAlphabet,
  gridSize,
  gridText,
  type SquareFill,
  squarePoint,
} from "@/lib/tool-kit/ciphers-polygraphic/squares";

/**
 * The bifid cipher (Félix Delastelle).
 *
 * A Polybius square turns each letter into two coordinates, a row and a column. Bifid
 * writes the message's row numbers in one line and its column numbers beneath, reads the
 * digits off *across* — all the rows, then all the columns — and cuts that stream into
 * pairs, each pair naming a cipher letter in the same square. Every cipher letter
 * therefore depends on two plain letters: the row of one and the column of another.
 *
 * Long messages are cut into blocks of `period` letters, and each block is enciphered
 * on its own; a period of 0 treats the whole message as one block, as Delastelle's
 * original did. Decoding turns each cipher letter back into its two coordinates, lays the
 * stream out again, and reads its first half as rows and its second half as columns.
 */

export interface BifidOptions {
  keyword: string;
  kind: GridAlphabet;
  fill: SquareFill;
  /** Letters per block; 0 for the whole message as one block. */
  period: number;
}

/** One block of the message with every intermediate line, for the working table. */
export interface BifidBlock {
  /** The block as the plain text of the pair (the output when decoding). */
  plain: string;
  /** The block as the cipher text of the pair (the output when encoding). */
  cipher: string;
  /** Row and column of each plain letter, 1-based. */
  rows: number[];
  columns: number[];
  /** Rows then columns: the stream the cipher letters are read from. */
  stream: number[];
}

export interface BifidResult {
  square: string;
  size: number;
  blocks: BifidBlock[];
  /** The result as one run of capitals. */
  output: string;
  droppedQ: number;
}

function splitBlocks(chars: string, period: number): string[] {
  if (period < 1 || period >= chars.length) {
    return chars === "" ? [] : [chars];
  }
  const blocks: string[] = [];
  for (let start = 0; start < chars.length; start += period) {
    blocks.push(chars.slice(start, start + period));
  }
  return blocks;
}

function coordinates(square: string, size: number, letters: string) {
  const rows: number[] = [];
  const columns: number[] = [];
  for (const letter of letters) {
    const point = squarePoint(square, size, letter);
    rows.push(point.row + 1);
    columns.push(point.column + 1);
  }
  return { rows, columns };
}

function letterAt(square: string, size: number, row: number, column: number) {
  return square.charAt((row - 1) * size + (column - 1));
}

function encipherBlock(
  square: string,
  size: number,
  plain: string
): BifidBlock {
  const { rows, columns } = coordinates(square, size, plain);
  const stream = [...rows, ...columns];
  let cipher = "";
  for (let index = 0; index < stream.length; index += 2) {
    cipher += letterAt(square, size, stream[index], stream[index + 1]);
  }
  return { plain, cipher, rows, columns, stream };
}

function decipherBlock(
  square: string,
  size: number,
  cipher: string
): BifidBlock {
  const { rows: first, columns: second } = coordinates(square, size, cipher);
  const stream: number[] = [];
  for (const [index, row] of first.entries()) {
    stream.push(row, second[index]);
  }
  const half = cipher.length;
  const rows = stream.slice(0, half);
  const columns = stream.slice(half);
  let plain = "";
  for (const [index, row] of rows.entries()) {
    plain += letterAt(square, size, row, columns[index]);
  }
  return { plain, cipher, rows, columns, stream };
}

export function bifidTransform(
  text: string,
  options: BifidOptions,
  direction: CipherDirection
): BifidResult {
  const size = gridSize(options.kind);
  const square = buildSquare(options.keyword, options.kind, options.fill);
  const { chars, droppedQ } = gridText(text, options.kind);
  const blocks = splitBlocks(chars, options.period).map((block) =>
    direction === "encode"
      ? encipherBlock(square, size, block)
      : decipherBlock(square, size, block)
  );
  const output = blocks
    .map((block) => (direction === "encode" ? block.cipher : block.plain))
    .join("");
  return { square, size, blocks, output, droppedQ };
}
